import {
  fetchAllUserCoupons,
  formatCouponIsoLocalDateTime,
} from "@/api/coupon-user";
import {
  normalizeUserCoupon,
  COUPON_SCENE_AI_COPYTRADE,
  COUPON_USER_STATUS_AVAILABLE,
} from "@/components/Pages/Activity/Voucher/normalizeUserCoupon";

const CACHE_TTL_MS = 20000;

/** 用券下单成功后派发，通知各 OrderForm 强制刷新券列表 */
export const AICOPY_USABLE_VOUCHERS_INVALIDATE_EVENT = "aicopy-usable-vouchers-invalidate";

/** @type {{ key: string, ts: number, items: unknown[] } | null} */
let cache = null;

/** @type {Map<string, Promise<{ ok: boolean, items: unknown[], mc?: string }>>} */
const inflight = new Map();

/** 清除短缓存（锁券/核销后券状态变化，避免仍展示已用券） */
export function invalidateAicopyUsableVouchersCache() {
  cache = null;
  inflight.clear();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AICOPY_USABLE_VOUCHERS_INVALIDATE_EVENT));
  }
}

/**
 * AI 跟单用券列表（voucherapi.md §3.2）：
 * `status=AVAILABLE` 仅拉可用券（不含 LOCKED/已核销/过期）；
 * `scene=AI_COPYTRADE` 仅返回适用场景含 AI 跟单的券；
 * `expireAtFrom` 为当前时刻；`sortBy=expireAt`、`sortOrder=desc`。
 * 多策略卡挂载多个 OrderForm 时共用短缓存与进行中单例。
 *
 * @param {{ tenantId: number|string, userId: number|string, maxItems?: number, forceRefresh?: boolean }} opts
 */
export function fetchAicopyUsableVouchersCached({
  tenantId,
  userId,
  maxItems = 40,
  forceRefresh = false,
} = {}) {
  const key = `${tenantId ?? ""}:${String(userId ?? "")}:${COUPON_SCENE_AI_COPYTRADE}`;
  const now = Date.now();
  if (!forceRefresh && cache && cache.key === key && now - cache.ts < CACHE_TTL_MS) {
    return Promise.resolve({ ok: true, items: cache.items });
  }
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = (async () => {
    try {
      const nowIso = formatCouponIsoLocalDateTime(new Date());
      const listExtras = {
        status: COUPON_USER_STATUS_AVAILABLE,
        scene: COUPON_SCENE_AI_COPYTRADE,
        sortBy: "expireAt",
        sortOrder: "desc",
        ...(nowIso ? { expireAtFrom: nowIso } : {}),
      };
      const { ok, items, mc } = await fetchAllUserCoupons(
        { tenantId, userId: String(userId), pageSize: 50, maxPages: 20, ...listExtras },
        {}
      );
      if (!ok) {
        return { ok: false, items: [], mc };
      }
      let normalized = (items || [])
        .map(normalizeUserCoupon)
        .filter(Boolean)
        .filter((row) => String(row.status || "").toUpperCase() === COUPON_USER_STATUS_AVAILABLE);
      normalized = normalized.slice(0, maxItems);
      cache = { key, ts: Date.now(), items: normalized };
      return { ok: true, items: normalized };
    } catch (e) {
      return { ok: false, items: [], mc: e?.mc };
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}
